import type { ConversationListItemData } from '../../hooks/use-conversations';

import { Avatar, colorForSeed, initialsFor } from './Avatar';
import { StatusPill, type ConversationStatus } from './StatusPill';
import { shortRelativeTime } from './format-time';

interface Props {
  item: ConversationListItemData;
  selected: boolean;
  onSelect: () => void;
  onPopOut: () => void;
}

// v1 conversations don't carry an "agent" yet; derive a stable display name
// from the title's leading noun so the list has varied avatars as in the
// prototype. When the backend adds an agent field we can read that instead.
function derivedAgentName(title: string): string {
  const first = title.trim().split(/\s+/)[0] ?? 'Agent';
  return `${first.slice(0, 1).toUpperCase()}${first.slice(1)} Agent`;
}

function deriveStatus(item: ConversationListItemData): ConversationStatus {
  if (item.processing_status === 'processing') return 'working';
  if (item.unread) return 'awaiting-input';
  return 'idle';
}

export function ConversationListItem({ item, selected, onSelect, onPopOut }: Props) {
  const agent = derivedAgentName(item.title);
  const agentColor = colorForSeed(agent);
  const status = deriveStatus(item);
  const time = shortRelativeTime(item.updated_at);

  return (
    <div
      onClick={onSelect}
      onDoubleClick={onPopOut}
      style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--border-soft)',
        cursor: 'pointer',
        background: selected ? 'var(--accent-soft)' : 'transparent',
        borderLeft: selected ? '2px solid var(--accent)' : '2px solid transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Avatar initials={initialsFor(agent)} color={agentColor} size={20} />
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-soft)',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {agent}
        </span>
        {time && <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>{time}</span>}
        {item.unread && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--accent)',
            }}
          />
        )}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: item.unread ? 600 : 500,
          color: 'var(--text)',
          marginBottom: 4,
          lineHeight: 1.35,
        }}
      >
        {item.title}
      </div>
      {item.preview && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-soft)',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.preview}
        </div>
      )}
      <div style={{ marginTop: 6 }}>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

export { derivedAgentName, deriveStatus };
